import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, OverlayTrigger, Tooltip, Row, Col,
} from 'react-bootstrap';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import TextareaAutosize from 'react-autosize-textarea';
import { putRequest } from '../../requestUtils';
import { analyticsTrack } from '../../analyticsUtils';
import { timezoneStringLabel } from '../../dateUtils';
import CalendarOptions from '../common/CalendarOptions';
import TimezoneSelector from '../common/TimezoneSelector';
import './ScheduleInterviewConsultant.scss';

const datesGroupedByMonth = (days) => {
  const hash = {};

  days.forEach((el) => {
    if (!hash[el.month()]) {
      hash[el.month()] = [];
    }
    hash[el.month()].push(el);
  });
  return hash;
};

const adjustTimeToZone = (time, tz1, tz2) => utcToZonedTime(zonedTimeToUtc(time, tz1), tz2);

const convertedAvailabilities = (availabilities, tz1, tz2) => availabilities.map((availability) => {
  const d = moment(`${availability.date} ${availability.start_time}`).toDate();
  const adjusted = adjustTimeToZone(d, tz1, tz2);
  const m = moment(adjusted);
  return ({
    date: m.format('YYYY-MM-DD'),
    start_time: m.format('HH:mm'),
    initial_value: d.valueOf(),
    moment: m,
  });
});

class ScheduleInterviewConsultant extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timezone: this.props.timezone,
      showModal: false,
      showSendNotificationModal: false,
      selectedDate: null,
      selectedTime: null,
      schedulingIssue: false,
      rescheduleNote: '',
    };

    this.location = 'Consultant Interview Booking Page';

    this.handleTimezoneChange = this.handleTimezoneChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDateSelect = this.handleDateSelect.bind(this);
    this.handleTimeSelect = this.handleTimeSelect.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleToggleSchedulingIssue = this.handleToggleSchedulingIssue.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  componentDidMount() {
    analyticsTrack(
      'User visited interview slot selection page for consultants',
      { location: this.location },
    );
  }

  ableToSubmit() {
    const {
      timezone, selectedDate, selectedTime, schedulingIssue,
    } = this.state;

    return schedulingIssue || (timezone && selectedDate && selectedTime);
  }

  validTimeExists() {
    const { timeSlots, interviewTimezone } = this.props;

    const now = new Date();

    return timeSlots.reduce((aggr, avail) => {
      const val = zonedTimeToUtc(
        moment(`${avail.date} ${avail.start_time}`).toDate(),
        interviewTimezone,
      ).valueOf();

      return aggr || (val > now.valueOf());
    }, false);
  }

  localTime() {
    const utcSeconds = (new Date()).valueOf() + ((new Date()).getTimezoneOffset() * 60 * 1000);
    return adjustTimeToZone(new Date(utcSeconds), 'UTC', this.state.timezone);
  }

  handleShowModal() {
    analyticsTrack("User clicked 'Send Time'", { location: this.location });
    this.setState({ showModal: true });
  }

  handleHideModal() {
    this.setState({ showModal: false });
  }

  handleDateSelect(e) {
    const { date } = e.target.dataset;
    this.setState({ selectedDate: new Date(Number(date)) });
  }

  handleTimeSelect(e) {
    analyticsTrack('User selected time', { location: this.location });

    const dateTime = e.target.dataset.time;
    this.setState({ selectedTime: new Date(Number(dateTime)) });
  }

  handleTimezoneChange(newTimezone) {
    const { timezone } = this.state;
    if (timezone === null) {
      analyticsTrack('User selected timezone', { location: this.location });
    }

    this.setState({ timezone: newTimezone, selectedDate: null, selectedTime: null });
  }

  handleCloseModal() {
    this.setState({ showModal: false, errors: null });
  }

  handleToggleSchedulingIssue() {
    const { schedulingIssue } = this.state;
    if (!schedulingIssue) {
      analyticsTrack("User checked 'No Times Work'", { location: this.location });
    }

    this.setState({ schedulingIssue: !this.state.schedulingIssue });
  }

  handleInputChange(value) {
    this.setState({ rescheduleNote: value });
  }

  handleSubmit() {
    const {
      updateInterviewUrl,
      csrfToken,
      proposalResourceId,
      interviewLength,
      proposalUrl,
    } = this.props;
    const {
      schedulingIssue, selectedTime, timezone, rescheduleNote,
    } = this.state;

    const startTime = moment(selectedTime).format('HH:mm');
    const endTime = moment(selectedTime).add(interviewLength, 'minutes').format('HH:mm');
    const date = moment(selectedTime).format('YYYY-MM-DD');

    const params = {
      proposal_resource_id: proposalResourceId,
      reschedule_requested: schedulingIssue,
      reschedule_note: rescheduleNote,
      interview: {
        accepted_time: {
          date,
          start_time: startTime,
          end_time: endTime,
        },
        timezone,
      },
    };

    putRequest(
      updateInterviewUrl,
      params,
      csrfToken,
    ).then(() => {
      analyticsTrack('User selected interview time', { location: this.location });
      window.location.href = proposalUrl;
    }).catch((error) => {
      this.setState({ errors: error });
      return null;
    });
  }

  renderErrors() {
    const { errors } = this.state;

    return (errors
      && (
      <div id="error_explanation">
        <h3>We&apos;re sorry, this timeslot is no longer available. Please choose another.</h3>
      </div>
      )
    );
  }

  renderTimezoneSelect() {
    const { timezone } = this.state;

    return (
      <div className="timezone-select mt-4">
        <div className="timezone-select-label body-text-2 medium-weight mb-2">
          1. Select your time zone
        </div>
        <TimezoneSelector
          onChange={(newTz) => this.handleTimezoneChange(newTz)}
          selectedValue={timezone}
        />
      </div>
    );
  }

  renderCalendarOptions() {
    const { schedulingIssue, selectedDate, timezone } = this.state;
    const { timeSlots, interviewTimezone } = this.props;
    const converted = convertedAvailabilities(timeSlots, interviewTimezone, timezone);
    const days = converted.filter(
      (ts) => ts.moment > this.localTime().valueOf() + 3600000,
    ).map((a) => (moment(a.date)));
    const groupedDates = datesGroupedByMonth(days);
    let months = [];

    Object.keys(groupedDates).forEach((month) => { months.push(month); });

    months = months.sort((a, b) => groupedDates[a][0].valueOf() - groupedDates[b][0].valueOf());

    return (
      <div className={`calendar-options-component-container body-text-2 ${schedulingIssue ? 'disabled' : ''}`}>
        2. Select the day that fits your schedule among the available ones
        <div className="calendar-container">
          {months.map((month) => (
            <CalendarOptions
              key={moment(groupedDates[month][0]).startOf('month').toDate()}
              startDate={moment(groupedDates[month][0]).startOf('month').toDate()}
              availableDays={groupedDates[month].map((date) => date.date())}
              updateSelection={this.handleDateSelect}
              selectedDate={selectedDate}
              disabled={schedulingIssue}
            />
          ))}
          <div className="legend">
            <div className="available-times">&nbsp;&nbsp;Available times</div>
            <div className="selected-day">&nbsp;&nbsp;Selected day</div>
            <div className="no-times-available">&nbsp;&nbsp;No times on that day</div>
          </div>
        </div>
      </div>
    );
  }

  renderTimeOptions() {
    const { timeSlots, interviewTimezone } = this.props;
    const {
      schedulingIssue, selectedDate, selectedTime, timezone,
    } = this.state;
    const correctedSelectedDate = new Date(
      selectedDate.getTime() - (selectedDate.getTimezoneOffset() * 60000),
    );
    const timeSlotsForChosenDay = convertedAvailabilities(timeSlots, interviewTimezone, timezone)
      .filter((ts) => (ts.date && ts.date === correctedSelectedDate.toISOString().slice(0, 10))
          && (ts.moment > this.localTime().valueOf() + 3600000))
      .sort((a, b) => a.moment.valueOf() - b.moment.valueOf());
    const adjustedFirstTimeSlot = selectedDate
      && moment(`${timeSlotsForChosenDay[0].date} ${timeSlotsForChosenDay[0].start_time}`);
    return (
      <div className={`time-options-component-container ${schedulingIssue ? 'disabled' : ''}`}>
        <div className="body-text-2 medium-weight">
          <br />
          3. Pick a time to meet on
          {' '}
          {adjustedFirstTimeSlot.format('dddd, MMM Do')}
          <br />
          <Row className="time-options">
            {timeSlotsForChosenDay.map((ts) => {
              const time = moment(`${ts.date} ${ts.start_time}`);
              const selected = ((!schedulingIssue && selectedTime && ts.initial_value === selectedTime.valueOf()) ? 'selected' : '');

              return (
                <Col xs={6} sm={4} md={3} lg={2}>
                  <div
                    className={`interview-time-option ${selected}`}
                    data-time={ts.initial_value}
                    onClick={this.handleTimeSelect}
                    role="button"
                    tabIndex="0"
                  >
                    {time.format('hh:mm A')}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
    );
  }

  renderSchedulingIssueCheckbox() {
    return (
      <div className="scheduling-issue">
        <label className="custom-checkbox-container" htmlFor="schedulingIssue">
          <input
            type="checkbox"
            onClick={this.handleToggleSchedulingIssue}
            id="schedulingIssue"
          />
          <span className="checkmark" />
          <span className="checkbox-text">I did not find a day and a time that works for me.</span>
          <div className="checkbox-subheading">
            Employer will be notified to provide new timeslots.
          </div>
        </label>
      </div>
    );
  }

  renderSubmitButton() {
    const disabled = !this.ableToSubmit();
    const { schedulingIssue } = this.state;
    const tooltipCopy = 'Please pick an interview time.';

    const onSubmit = () => !disabled && this.handleShowModal();

    const submitButton = () => (
      <button
        className={`btn btn-primary submit-button ${disabled && 'disabled'}`}
        onClick={onSubmit}
        // can't use disabled prop here because a disabled button also disables the OverlayTrigger
        // instead, we prefer to check !disabled ourselves
      >
        {schedulingIssue ? 'Request Reschedule' : 'Send Time'}
      </button>
    );

    const possiblyWrappedButton = () => (
      disabled
        ? (
          <OverlayTrigger
            placement="left"
            delay={200}
            overlay={(
              <Tooltip id="cannot-submit-tooltip">
                {tooltipCopy}
              </Tooltip>
          )}
          >
            {submitButton()}
          </OverlayTrigger>
        )
        : submitButton()
    );

    return (
      <div
        className="submit-button-container g-row--always g-row-justify-right g-flex-align-center"
      >
        <br />
        {possiblyWrappedButton()}
      </div>
    );
  }

  renderConfirmationModal() {
    const {
      schedulingIssue, selectedTime, showModal, timezone, rescheduleNote,
    } = this.state;
    const { projectName, interviewLength, interviewTimezone } = this.props;
    const adjustedStartTime = adjustTimeToZone(
      selectedTime || new Date(),
      interviewTimezone,
      timezone,
    );
    const formattedDate = moment(adjustedStartTime).format('ddd, MMM D YYYY');
    const formattedStartTime = moment(adjustedStartTime).format('h:mm');
    const endTime = moment(adjustedStartTime).add(interviewLength, 'minutes');
    const formattedEndTime = moment(endTime).format('h:mm A');
    const formattedTimeZone = (timezone ? timezoneStringLabel(timezone) : null);
    const heading = (schedulingIssue
      ? 'We will reschedule this interview' : 'You are about to schedule your interview'
    );

    return (
      <Modal
        className="confirm-interview"
        show={showModal}
        onHide={this.handleHideModal}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          {this.renderErrors()}
          <div className="modal-headline">{heading}</div>
          {!schedulingIssue
            && (
            <div className="modal-subtext">
              Conference meeting link will be shared via email by Clora.
            </div>
            )}
          {schedulingIssue
            ? (
              <Fragment>
                <div>
                  A notification will be sent indicating that the available times do not work for you.
                  Could you provide information regarding your availability
                  to help us with rescheduling the interview?
                </div>
                <TextareaAutosize
                  className="interview-reschedule-note"
                  id="interview-reschedule-note"
                  rows={2}
                  value={rescheduleNote}
                  onChange={(e) => this.handleInputChange(e.target.value)}
                  placeholder="Please enter reschedule reason"
                />
              </Fragment>
            )
            : (
              <div className="modal-details">
                <div className="time-details">
                  {`${formattedDate} - ${formattedStartTime} - ${formattedEndTime}`}
                </div>
                <span className="timezone-text">{formattedTimeZone}</span>
                <br />
                {projectName}
              </div>
            )}
          <br />
          <Row className="center">
            <Col xs={6}>
              <button className="btn btn-secondary modal-button" onClick={this.handleCloseModal}>
                Back to times
              </button>
            </Col>
            <Col xs={6}>
              <button
                className="btn btn-primary modal-button"
                onClick={this.handleSubmit}
                disabled={schedulingIssue && !rescheduleNote}
              >
                {schedulingIssue ? 'Submit' : 'Yes, Schedule'}
              </button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    );
  }

  render() {
    const { projectName, interviewLength } = this.props;
    const { timezone, selectedDate, schedulingIssue } = this.state;

    if (!this.validTimeExists()) {
      return (
        <div className="expired-message">
          <div className="header">Contact Clora for assistance.</div>
          <div className="subtext">
            It looks like the available times have expired and are no
            longer available. Please reach out to Clora for assistance.
          </div>
        </div>
      );
    }
    return (
      <div className="consultants-interview-scheduler-container">
        <div className="header style-guide">
          <div className="title">
            {`Schedule your interview: ${projectName}`}
          </div>
          <div className="subtext">
            {interviewLength}
            {' '}
            mins
          </div>
        </div>
        <div className="padded-modal-content">
          {this.renderTimezoneSelect()}
          {timezone && this.renderCalendarOptions()}
          {selectedDate && this.renderTimeOptions()}
          {timezone && this.renderSchedulingIssueCheckbox()}
          {(selectedDate || schedulingIssue) && this.renderSubmitButton()}
          {this.renderConfirmationModal()}
        </div>
      </div>
    );
  }
}

ScheduleInterviewConsultant.propTypes = {
  updateInterviewUrl: PropTypes.string.isRequired,
  proposalUrl: PropTypes.string.isRequired,
  proposalResourceId: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  interviewTimezone: PropTypes.string,
  timeSlots: PropTypes.arrayOf(PropTypes.shape(
    { date: PropTypes.string, start_time: PropTypes.string, end_time: PropTypes.string },
  )).isRequired,
  projectName: PropTypes.string.isRequired,
  interviewLength: PropTypes.number.isRequired,
};

ScheduleInterviewConsultant.defaultProps = {
  timezone: null,
  interviewTimezone: null,
};

export default ScheduleInterviewConsultant;
